import { IS_DEVTOOLS_ENABLED } from '@/constants';

/**
 * @description Get the hostname of the current page
 * @returns The hostname of the current page
 */
const getHostname = () => {
	if (typeof window === 'undefined') {
		return '';
	}

	return window.location.hostname;
};

/**
 * @description Check if the current domain is a dev domain
 * @returns True if the current domain is a dev domain
 */
export const isDevDomain = (): boolean => {
	const hostname = getHostname();
	if (!hostname || hostname.startsWith('.') || hostname.endsWith('.')) {
		return false;
	}
	// Exact match for dev domain and its subdomains
	return hostname === 'dev-rvlife.com' || hostname.endsWith('.dev-rvlife.com');
};

/**
 * @description Check if the current domain is a staging domain
 * @returns True if the current domain is a staging domain
 */
export const isStagingDomain = (): boolean => {
	const hostname = getHostname();
	if (!hostname || hostname.startsWith('.') || hostname.endsWith('.')) {
		return false;
	}
	// Exact match for staging domain and its subdomains
	return hostname === 'staging-rvlife.com' || hostname.endsWith('.staging-rvlife.com');
};

/**
 * Validates that a path is a safe internal (same-origin, relative) path.
 * Prevents open-redirect attacks by rejecting absolute URLs and protocol-relative paths.
 * @param path - The path to validate
 * @returns True if the path is a safe internal path, false otherwise
 */
export const isSafeInternalPath = (path: string | null | undefined): path is string => {
	if (!path || typeof path !== 'string') return false;
	if (!path.startsWith('/')) return false;
	if (path.startsWith('//')) return false;
	if (path.includes('://')) return false;

	try {
		const parsed = new URL(path, 'http://localhost');
		return parsed.origin === 'http://localhost';
	} catch {
		return false;
	}
};

/**
 * Retrieves the origin from a given URL
 * @param url - The URL to extract the origin from
 * @returns The origin of the URL
 */
export const getOriginFromUrl = (url: string): string => {
	const urlObj = new URL(url);
	return urlObj.origin;
};

/**
 * @description Generate the remote entry URL
 * @param baseUrl The base URL of the remote entry
 * @param version The version of the remote entry
 * @returns The remote entry URL
 */
export const generateRemoteEntryUrl = (baseUrl: string, version: string): string => {
	const isDevOrStaging = isDevDomain() || isStagingDomain();
	const trimmedVersion = version?.trim?.() || '';

	if (trimmedVersion && IS_DEVTOOLS_ENABLED && isDevOrStaging) {
		// Edited version should only use the origin from the url, not the baseUrl (Base URL may contain MFE versions)
		// Example: https://auth.dev-rvlife.com/v3.6.0
		return `${getOriginFromUrl(baseUrl)}/${trimmedVersion}/remoteEntry.js`;
	}

	return `${baseUrl}/remoteEntry.js`;
};
